import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Printer, Settings, Play,
  Minimize2, Maximize2, Maximize, Minimize, Users, Download, X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAppointmentsCalendarQuery, useLazyAppointmentsCalendarQuery, useGetAppointmentQuery } from '../../api/appointmentsApi';
import { useLazyCalendarExportQuery } from '../../api/reportsApi';
import { exportCalendarPdf } from '../../utils/exporters';
import { useListDoctorsQuery } from '../../api/usersApi';
import { useGetPatientQuery } from '../../api/patientsApi';
import { useClinicSettingsQuery } from '../../api/settingsApi';
import { doctorDisplayName } from './doctorColors';
import CalendarGrid from './CalendarGrid';
import DoctorPanel from './DoctorPanel';
import TodayScheduleSidebar from './TodayScheduleSidebar';
import AppointmentPopover from './AppointmentPopover';
import AppointmentFormModal from './AppointmentFormModal';
import CollectPaymentModal from './CollectPaymentModal';
import { procedureLabel } from './appointmentStatus';
import './calendar.css';

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

function formatDateRange(start, end) {
  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) return start.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const startStr = start.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: sameMonth && sameYear ? undefined : 'short' });
  const endStr = end.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

const COL_WIDTH_KEY = 'healing.calendar.columnWidth';
const COL_WIDTHS = ['compact', 'normal', 'wide'];
const VIEW_KEY = 'healing.calendar.view';
const VIEWS = [
  { key: 'day',    label: 'Day' },
  { key: '3day',   label: '3-Day' },
  { key: 'week',   label: 'Week' },
  { key: 'doctor', label: 'Doctor' },
];

function viewDayCount(view) {
  switch (view) {
    case 'day':      return 1;
    case '3day':     return 3;
    case 'week':     return 6;
    case 'doctor':   return 1;
    default:         return 6;
  }
}

export default function AppointmentsPage() {
  const [view, setView] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(VIEW_KEY) : null;
    return VIEWS.some((v) => v.key === saved) ? saved : 'week';
  });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* */ } }, [view]);

  const [rangeStart, setRangeStart] = useState(() => startOfDay(new Date()));
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [columnWidth, setColumnWidth] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(COL_WIDTH_KEY) : null;
    return COL_WIDTHS.includes(saved) ? saved : 'normal';
  });
  useEffect(() => { try { localStorage.setItem(COL_WIDTH_KEY, columnWidth); } catch { /* */ } }, [columnWidth]);

  // Modal / popover state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [formInitial, setFormInitial] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);

  // Deep link of the form /appointments?openId=<guid> — e.g. clicking an appointment from global
  // search. Fetched directly by id so the popover opens instantly regardless of which week/day the
  // calendar currently has loaded — no need to jump the visible range to find it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('openId');
  const { data: deepLinkedAppointment } = useGetAppointmentQuery(openId, { skip: !openId });
  useEffect(() => {
    if (deepLinkedAppointment) setSelectedAppointment(deepLinkedAppointment);
  }, [deepLinkedAppointment]);
  const closeAppointmentPopover = () => {
    setSelectedAppointment(null);
    if (searchParams.get('openId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
  };

  const visibleDays = viewDayCount(view);
  const rangeEnd = useMemo(() => addDays(rangeStart, visibleDays - 1), [rangeStart, visibleDays]);

  const [showExport, setShowExport] = useState(false);

  const { data: doctors = [] } = useListDoctorsQuery();
  const { data: clinicSettings } = useClinicSettingsQuery();
  const toMin = (t) => { if (!t) return null; const p = String(t).split(':'); return Number(p[0]) * 60 + Number(p[1]); };
  const breakStartMin = toMin(clinicSettings?.breakStart);
  const breakEndMin = toMin(clinicSettings?.breakEnd);

  const { data: appointments = [], refetch: refetchAppts } = useAppointmentsCalendarQuery(
    {
      from: rangeStart.toISOString(),
      to: addDays(rangeEnd, 1).toISOString(),
    },
    // Re-pull if the cached data is older than 30 s when the page mounts or the date window changes.
    { refetchOnMountOrArgChange: 30 },
  );

  const { data: paymentPatient } = useGetPatientQuery(paymentTarget?.patientId, {
    skip: !paymentTarget?.patientId,
  });

  const appointmentCounts = useMemo(() => {
    const counts = { all: appointments.length };
    appointments.forEach((a) => {
      counts[a.assignedUserId] = (counts[a.assignedUserId] || 0) + 1;
    });
    return counts;
  }, [appointments]);

  const doctorNameById = useMemo(() => {
    const m = new Map();
    doctors.forEach((d) => m.set(d.id, d.fullName));
    return m;
  }, [doctors]);

  // Navigation
  const stepBack    = () => setRangeStart(addDays(rangeStart, -visibleDays));
  const stepForward = () => setRangeStart(addDays(rangeStart, visibleDays));
  const goToday     = () => setRangeStart(startOfDay(new Date()));

  // Slot click → open form modal prefilled with date/time/doctor
  const handleSlotClick = (date, hour, minute, opts = {}) => {
    const d = new Date(date);
    d.setHours(hour, minute, 0, 0);
    setFormInitial({
      date: d.toISOString(),
      doctorId: opts.doctorId ?? selectedDoctorId,
      duration: 60,
    });
    setShowForm(true);
  };

  const handleAppointmentClick = (a) => setSelectedAppointment(a);

  // The click handler stores a SNAPSHOT of the row. On its own that snapshot never changes, so
  // anything mutated while the popover is open (session type, status, duration) saved correctly
  // but kept rendering the stale value — the popover's own controlled inputs read from this object.
  // Re-resolve it against the live calendar cache on every render so optimistic updates and
  // refetches both land. Falls back to the snapshot when the row is no longer in the calendar
  // (cancelling or rescheduling removes it), so the popover never blanks out mid-action.
  const liveAppointment = useMemo(() => {
    if (!selectedAppointment) return null;
    const fresh = appointments.find((a) => a.id === selectedAppointment.id);
    return fresh ? { ...selectedAppointment, ...fresh } : selectedAppointment;
  }, [selectedAppointment, appointments]);

  const handleAddWalkin = () => {
    const now = new Date();
    setFormInitial({
      date: now.toISOString(),
      doctorId: selectedDoctorId,
      duration: 60,
    });
    setShowForm(true);
  };

  const handleBlockCalendar = () => {
    setFormInitial({
      date: new Date().toISOString(),
      doctorId: selectedDoctorId,
      duration: 60,
      defaultTab: 'block',
    });
    setShowForm(true);
  };

  const handleCollectPayment = (appt, patient) => {
    setSelectedAppointment(null);
    setPaymentTarget({ patientId: appt.patientId, patient, appointment: appt });
  };

  // Press Escape to exit fullscreen
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  return (
    <div className={`cal-page${isFullscreen ? ' is-fullscreen' : ''}`}>
      <div className="cal-header">
        <div className="cal-header-title">
          <h1>Calendar</h1>
          <span className="cal-clinic">The Healing Clinic</span>
          <button className="btn btn-sm btn-outline" style={{ marginLeft: 12 }}>
            <Play size={12} /> Learn
          </button>
        </div>
        <div className="cal-header-spacer" />

        <div className="cal-date-nav">
          <button className="cal-nav-btn" onClick={stepBack} aria-label="Previous"><ChevronLeft size={16} /></button>
          <span className="cal-date-label">{formatDateRange(rangeStart, rangeEnd)}</span>
          <button className="cal-nav-btn" onClick={stepForward} aria-label="Next"><ChevronRight size={16} /></button>
        </div>

        <button className="btn btn-sm btn-outline" onClick={goToday}>Today</button>

        <div className="cal-view-toggle">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className={view === v.key ? 'active' : ''}
              onClick={() => setView(v.key)}
              title={v.key === 'doctor' ? 'Doctor-wise view (one column per doctor)' : v.label}
            >
              {v.key === 'doctor' ? <Users size={13} /> : v.label}
            </button>
          ))}
        </div>

        {/* Column width: cycles Compact → Normal → Wide and back */}
        <div className="cal-view-toggle" title="Column width">
          <button
            className={columnWidth === 'compact' ? 'active' : ''}
            onClick={() => setColumnWidth('compact')}
            title="Compact columns"
          ><Minimize2 size={13} /></button>
          <button
            className={columnWidth === 'normal' ? 'active' : ''}
            onClick={() => setColumnWidth('normal')}
            title="Normal columns"
          >M</button>
          <button
            className={columnWidth === 'wide' ? 'active' : ''}
            onClick={() => setColumnWidth('wide')}
            title="Wide columns — best for busy days"
          ><Maximize2 size={13} /></button>
        </div>

        <button
          className="cal-header-icon"
          title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand to fullscreen'}
          onClick={() => setIsFullscreen((f) => !f)}
        >
          {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        </button>
        <button className="cal-header-icon" title="Export appointments report" onClick={() => setShowExport(true)}><Download size={16} /></button>
        <button className="cal-header-icon" title="Print"><Printer size={16} /></button>
        <button className="cal-header-icon" title="Settings"><Settings size={16} /></button>
      </div>

      <div className="cal-body">
        <DoctorPanel
          doctors={doctors}
          appointmentCounts={appointmentCounts}
          selectedDoctorId={selectedDoctorId}
          onSelect={setSelectedDoctorId}
          onBlockCalendar={handleBlockCalendar}
        />

        <div className="cal-center">
          <CalendarGrid
            view={view}
            rangeStart={rangeStart}
            appointments={appointments}
            selectedDoctorId={selectedDoctorId}
            doctors={doctors}
            columnWidth={columnWidth}
            onSlotClick={handleSlotClick}
            onAppointmentClick={handleAppointmentClick}
            breakStartMin={breakStartMin}
            breakEndMin={breakEndMin}
            breakLabel="Break"
          />
        </div>

        <TodayScheduleSidebar
          appointments={appointments}
          onAppointmentClick={handleAppointmentClick}
          onAddWalkin={handleAddWalkin}
        />
      </div>

      {liveAppointment && (
        <AppointmentPopover
          appointment={liveAppointment}
          doctorName={liveAppointment.assignedUserName ?? doctorNameById.get(liveAppointment.assignedUserId)}
          allAppointments={appointments}
          onClose={closeAppointmentPopover}
          onCollectPayment={handleCollectPayment}
          onEdit={() => {
            setShowForm(true);
            setFormInitial({
              date: liveAppointment.scheduledAt,
              doctorId: liveAppointment.assignedUserId,
              duration: liveAppointment.durationMinutes,
            });
            setSelectedAppointment(null);
          }}
        />
      )}

      {showForm && (
        <AppointmentFormModal
          initialDate={formInitial?.date}
          initialDoctorId={formInitial?.doctorId}
          initialDurationMinutes={formInitial?.duration}
          defaultTab={formInitial?.defaultTab}
          allAppointments={appointments}
          onClose={() => { setShowForm(false); setFormInitial(null); }}
          onCreated={() => refetchAppts()}
        />
      )}

      {paymentTarget && (
        <CollectPaymentModal
          patient={paymentPatient ?? paymentTarget.patient}
          appointment={paymentTarget.appointment}
          doctorName={paymentTarget.appointment?.assignedUserName ?? doctorNameById.get(paymentTarget.appointment?.assignedUserId)}
          onClose={() => setPaymentTarget(null)}
        />
      )}

      {showExport && (
        <ExportAppointmentsModal
          defaultStart={rangeStart}
          defaultEnd={rangeEnd}
          doctorNameById={doctorNameById}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}

// ── Export appointments (day / date range) → CSV ──────────────────────────────
const csvCell = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
function downloadCsv(name, content) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
function toDateInput(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function ExportAppointmentsModal({ defaultStart, defaultEnd, doctorNameById, onClose }) {
  const [from, setFrom] = useState(toDateInput(defaultStart));
  const [to, setTo] = useState(toDateInput(defaultEnd));
  const [busy, setBusy] = useState(false);
  const [trigger] = useLazyAppointmentsCalendarQuery();
  const [triggerCalExport] = useLazyCalendarExportQuery();

  // Practitioner-grouped executive PDF (doctor blocks, summaries, totals, footer).
  const runPdf = async () => {
    const start = startOfDay(new Date(from));
    const end = startOfDay(new Date(to));
    if (end < start) { toast.error('“To” date must be on or after “From” date.'); return; }
    setBusy(true);
    try {
      const data = await triggerCalExport({ from: start.toISOString(), to: addDays(end, 1).toISOString() }).unwrap();
      if (!data?.doctors?.length) { toast.info('No appointments in that date range.'); setBusy(false); return; }
      const sameDay = from === to;
      const rangeLabel = sameDay ? `Date: ${from}` : `Range: ${from} to ${to}`;
      exportCalendarPdf(`appointments_${from}${sameDay ? '' : `_to_${to}`}`, data, { title: 'Appointment Report', rangeLabel });
      toast.success('PDF generated.');
      onClose();
    } catch {
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const runExport = async () => {
    const start = startOfDay(new Date(from));
    const end = startOfDay(new Date(to));
    if (end < start) { toast.error('“To” date must be on or after “From” date.'); return; }
    setBusy(true);
    try {
      const rows = await trigger({ from: start.toISOString(), to: addDays(end, 1).toISOString() }).unwrap();
      if (!rows || rows.length === 0) { toast.info('No appointments in that date range.'); setBusy(false); return; }
      const head = ['Date', 'Start time', 'End time', 'Duration (min)', 'Patient', 'Phone', 'Doctor', 'Status', 'Procedure', 'Notes'];
      const sorted = [...rows].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
      const lines = [head.join(',')];
      for (const a of sorted) {
        const s = new Date(a.scheduledAt);
        const dur = a.durationMinutes ?? 30;
        const e = new Date(s.getTime() + dur * 60000);
        const doctor = a.assignedUserName ? doctorDisplayName(a.assignedUserName) : (doctorNameById.get(a.assignedUserId) || '');
        lines.push([
          s.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata' }),
          s.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }),
          e.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }),
          dur,
          a.patientName ?? '',
          a.phoneNumber ?? '',
          doctor,
          a.status ?? '',
          procedureLabel(a) ?? '',
          (a.notes ?? '').replace(/\s*\n\s*/g, ' '),
        ].map(csvCell).join(','));
      }
      const sameDay = from === to;
      downloadCsv(`appointments_${from}${sameDay ? '' : `_to_${to}`}.csv`, lines.join('\r\n'));
      toast.success(`Exported ${sorted.length} appointment${sorted.length === 1 ? '' : 's'}.`);
      onClose();
    } catch (err) {
      toast.error('Could not export appointments. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Export appointments</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 0 }}>
            Export all appointment details for a single day or a date range to a CSV file.
          </p>
          <div className="form-row">
            <div className="form-group"><label className="form-label">From</label>
              <input className="form-control" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">To</label>
              <input className="form-control" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => { const t = toDateInput(new Date()); setFrom(t); setTo(t); }}>Today</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => { setFrom(toDateInput(defaultStart)); setTo(toDateInput(defaultEnd)); }}>Current view</button>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-outline" onClick={runExport} disabled={busy}>
            <Download size={14} /> {busy ? 'Working…' : 'CSV'}
          </button>
          <button type="button" className="btn btn-primary" onClick={runPdf} disabled={busy}>
            <Download size={14} /> {busy ? 'Working…' : 'PDF (by doctor)'}
          </button>
        </div>
      </div>
    </div>
  );
}
